/**
 * Public, pre-auth marketing landing page (`/welkom-bezoeker`).
 *
 * Chromeless — renders its own public nav + footer, NOT the app shell.
 * Loader-free and instantly paintable. Every "data-rich" element here is
 * STATIC marketing copy from the `landing` i18n namespace — the app-preview
 * card and tutor bubble are clearly-decorative placeholders, never wired to a
 * live endpoint (there is no data on a pre-auth page). u-form Dutch throughout.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Spreekballon } from "../components/Spreekballon";
import { Button } from "../components/ui/Button";
import { SegmentedToggle } from "../components/ui/SegmentedToggle";
import { initI18n, currentLocale } from "../lib/i18n";
import { useT } from "../lib/use-t";

// ── Public NL/EN pill — cookie-only, no server persistence (no session here) ──

function FlagNL() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex flex-col w-[18px] h-[13px] rounded-[2.5px] overflow-hidden border border-black/10 flex-none"
    >
      <span className="flex-1 bg-[#c8102e]" />
      <span className="flex-1 bg-white" />
      <span className="flex-1 bg-[#21468b]" />
    </span>
  );
}

function PublicLangPill() {
  const t = useT();
  const [locale, setLocale] = useState<"nl" | "en">(
    currentLocale() === "en" ? "en" : "nl",
  );

  function pick(next: "nl" | "en") {
    if (next === locale) return;
    setLocale(next);
    // Why: public page sets only the cookie + re-inits i18n. The authed
    // /user/preferences POST requires a session and is wrong here.
    document.cookie = `a2easy_lang=${next}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    void initI18n(next);
  }

  return (
    <SegmentedToggle
      ariaLabel={t("landing.nav.langSwitchLabel")}
      value={locale}
      onChange={pick}
      options={[
        { value: "nl", label: t("landing.nav.langNl"), icon: <FlagNL /> },
        { value: "en", label: t("landing.nav.langEn") },
      ]}
    />
  );
}

// ── Logo + wordmark ───────────────────────────────────────────────────────────

function Brand({ size = 32 }: { size?: number }) {
  return (
    <Link to="/welkom-bezoeker" className="inline-flex items-center gap-2.5">
      <Spreekballon size={size} />
      <span className="font-display font-semibold text-[19px] tracking-tight">
        <span className="text-[var(--accent)]">A2</span> Easy
      </span>
    </Link>
  );
}

// ── Inline SVG icon set (no emoji-as-icon) ────────────────────────────────────

const ArrowRight = ({ className }: { className?: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className={className} aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const stripIcons = [
  <svg key="star" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.7 5.7 21l2.3-7.1-6-4.5h7.6z" /></svg>,
  <svg key="pulse" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 12h4l2 5 4-12 2 7h6" /></svg>,
  <svg key="check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 13l4 4L19 7" /></svg>,
  <svg key="cal" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>,
];

const featureIcons = [
  <svg key="mic" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 2a3 3 0 00-3 3v6a3 3 0 006 0V5a3 3 0 00-3-3z" /><path d="M5 11a7 7 0 0014 0M12 18v3" /></svg>,
  <svg key="chat" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 11.5a8.5 8.5 0 01-12.5 7.5L3 21l2-5.5A8.5 8.5 0 1121 11.5z" /></svg>,
  <svg key="chart" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 19V5M4 19h16M8 16l4-5 3 3 4-6" /></svg>,
  <svg key="book" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 5a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2H6a2 2 0 01-2-2z" /><path d="M9 9h6M9 13h6" /></svg>,
];

// ── App-preview card — decorative static placeholder, not a live component ─────

function AppPreviewCard() {
  const t = useT();
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute -inset-6 rounded-[var(--radius-lg)]"
        style={{ background: "radial-gradient(60% 60% at 70% 30%, var(--accent-soft), transparent 70%)" }}
      />
      <article
        aria-label={t("landing.preview.placeholderLabel")}
        className="relative rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] overflow-hidden shadow-[var(--shadow)]"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--panel-soft)] border border-[var(--border)]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--panel-soft)] border border-[var(--border)]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--panel-soft)] border border-[var(--border)]" />
          <span className="ml-2 font-mono-badge text-[11px] text-[var(--ink-faint)]">{t("landing.preview.barLabel")}</span>
        </div>
        <div className="p-5 sm:p-6">
          <span className="font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">{t("landing.preview.eyebrow")}</span>
          <h3 className="mt-1 text-[22px] font-display font-semibold tracking-tight">{t("landing.preview.title")}</h3>
          <p className="font-serif-italic text-[var(--ink-muted)] text-[15px] mt-2 mb-4">{t("landing.preview.desc")}</p>
          <span className="inline-flex items-center gap-2 bg-[var(--warm)] text-white rounded-[var(--radius)] px-4 py-2.5 font-semibold text-sm shadow-[var(--shadow-warm)]">
            {t("landing.preview.cta")}
            <ArrowRight className="w-4 h-4" />
          </span>
          <div className="mt-5 flex gap-3 items-start bg-[var(--accent-soft)] border border-[var(--border)] rounded-[var(--radius)] p-3.5">
            <span className="w-[30px] h-[30px] rounded-full bg-[var(--accent)] text-white grid place-items-center font-mono-badge text-xs flex-none">{t("landing.preview.feedbackAvatar")}</span>
            <p className="font-serif-italic text-sm text-[var(--ink)] m-0">{t("landing.preview.feedback")}</p>
          </div>
        </div>
      </article>
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────

function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="max-w-[60ch] mx-auto mb-10 sm:mb-12 text-left lg:text-center">
      <span className="block font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)] mb-3">{eyebrow}</span>
      <h2 className="text-[27px] sm:text-[34px] lg:text-[38px] font-display font-semibold tracking-tight">{title}</h2>
      {sub ? <p className="text-[var(--ink-muted)] text-[15px] sm:text-[17px] mt-3.5">{sub}</p> : null}
    </div>
  );
}

// ── Public footer ─────────────────────────────────────────────────────────────

function PublicFooter() {
  const t = useT();
  return (
    <footer className="px-[22px] sm:px-11 py-8 sm:py-10 border-t border-[var(--border)] flex flex-wrap items-center gap-3 sm:gap-3.5 text-[var(--ink-faint)] text-[13px]">
      <Brand size={26} />
      <span className="hidden sm:inline ml-2">{t("landing.footer.copyright")}</span>
      <nav aria-label={t("landing.footer.navAriaLabel")} className="ml-auto flex gap-4 sm:gap-5">
        <Link to="/login" className="text-[var(--ink-muted)] hover:text-[var(--ink)]">{t("landing.footer.privacy")}</Link>
        <Link to="/login" className="hidden sm:inline text-[var(--ink-muted)] hover:text-[var(--ink)]">{t("landing.footer.terms")}</Link>
        <Link to="/login" className="text-[var(--ink-muted)] hover:text-[var(--ink)]">{t("landing.footer.contact")}</Link>
      </nav>
    </footer>
  );
}

// ── Public nav ────────────────────────────────────────────────────────────────

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  return (
    <>
      <a href="#hoe-het-werkt" onClick={onNavigate} className="text-[var(--ink-muted)] hover:text-[var(--ink)] font-medium text-[14.5px]">{t("landing.nav.howItWorks")}</a>
      <a href="#functies" onClick={onNavigate} className="text-[var(--ink-muted)] hover:text-[var(--ink)] font-medium text-[14.5px]">{t("landing.nav.features")}</a>
    </>
  );
}

function PublicNav() {
  const t = useT();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 border-b border-[var(--border)] bg-[var(--panel)]/70 backdrop-blur-md">
      <div className="flex items-center gap-3.5 px-[22px] sm:px-11 py-4 sm:py-5">
        <Brand />

        {/* Desktop cluster */}
        <div className="hidden lg:flex items-center gap-6 ml-auto">
          <NavLinks />
          <PublicLangPill />
          <Link to="/login" className="text-[var(--ink)] font-semibold text-[14.5px] hover:text-[var(--accent)]">{t("landing.nav.login")}</Link>
          <Button variant="primary" size="md" onClick={() => navigate("/signup")}>{t("landing.nav.signup")}</Button>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-label={t("landing.nav.menuLabel")}
          onClick={() => setMenuOpen((v) => !v)}
          className="lg:hidden ml-auto text-[var(--ink-muted)] p-2 -mr-2 rounded-[var(--radius)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </button>
      </div>

      {/* Mobile disclosure panel */}
      {menuOpen ? (
        <div className="lg:hidden px-[22px] pb-5 pt-1 flex flex-col gap-4 border-t border-[var(--border)] bg-[var(--panel)]">
          <nav className="flex flex-col gap-3 pt-3">
            <NavLinks onNavigate={() => setMenuOpen(false)} />
          </nav>
          <PublicLangPill />
          <div className="flex flex-col gap-2.5">
            <Button variant="secondary" size="md" className="w-full" onClick={() => { setMenuOpen(false); navigate("/login"); }}>{t("landing.nav.login")}</Button>
            <Button variant="primary" size="md" className="w-full" onClick={() => { setMenuOpen(false); navigate("/signup"); }}>{t("landing.nav.signup")}</Button>
          </div>
        </div>
      ) : null}
    </header>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function WelkomBezoekerPage() {
  const t = useT();
  const navigate = useNavigate();

  const steps = [
    { title: t("landing.how.step1Title"), body: t("landing.how.step1Body") },
    { title: t("landing.how.step2Title"), body: t("landing.how.step2Body") },
    { title: t("landing.how.step3Title"), body: t("landing.how.step3Body") },
  ];

  const features = [
    { title: t("landing.featureCards.card1Title"), body: t("landing.featureCards.card1Body"), warm: false },
    { title: t("landing.featureCards.card2Title"), body: t("landing.featureCards.card2Body"), warm: true },
    { title: t("landing.featureCards.card3Title"), body: t("landing.featureCards.card3Body"), warm: false },
    { title: t("landing.featureCards.card4Title"), body: t("landing.featureCards.card4Body"), warm: false },
  ];

  const stripItems = [
    t("landing.strip.item1"),
    t("landing.strip.item2"),
    t("landing.strip.item3"),
    t("landing.strip.item4"),
  ];

  return (
    <div className="min-h-dvh bg-[var(--bg)] text-[var(--ink)]">
      <PublicNav />

      {/* Hero */}
      <section className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-12 items-center px-[22px] sm:px-11 pt-10 sm:pt-[72px] pb-12 sm:pb-16 max-w-[1200px] mx-auto">
        <div>
          <span className="inline-flex items-center gap-2 bg-[var(--warm-soft)] text-[var(--warm)] rounded-full px-3.5 py-1.5 font-semibold text-[13px] mb-5 sm:mb-6">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.7 5.7 21l2.3-7.1-6-4.5h7.6z" /></svg>
            {t("landing.hero.pill")}
          </span>
          <h1 className="text-[38px] sm:text-5xl lg:text-[58px] font-display font-semibold tracking-tight leading-[1.05]">
            {t("landing.hero.titleA")}
            <span className="text-[var(--accent)]">{t("landing.hero.titleAccent")}</span>
            {t("landing.hero.titleB")}
          </h1>
          <p className="text-[16px] sm:text-[19px] text-[var(--ink-muted)] mt-4 sm:mt-5 max-w-[46ch] leading-relaxed">{t("landing.hero.lead")}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7 sm:mt-8">
            <Button variant="warm" size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
              {t("landing.hero.ctaPrimary")}
              <ArrowRight />
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="w-full sm:w-auto border border-[var(--border-strong)]"
              onClick={() => document.getElementById("hoe-het-werkt")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              {t("landing.hero.ctaSecondary")}
            </Button>
          </div>
          <div className="mt-5 hidden sm:flex items-center gap-4 text-[var(--ink-faint)] text-[13px]">
            <span>{t("landing.hero.trust1")}</span>
            <span className="w-1 h-1 rounded-full bg-[var(--ink-faint)]" />
            <span>{t("landing.hero.trust2")}</span>
            <span className="w-1 h-1 rounded-full bg-[var(--ink-faint)]" />
            <span>{t("landing.hero.trust3")}</span>
          </div>
        </div>

        <AppPreviewCard />
      </section>

      {/* Trust strip */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 px-[22px] sm:px-11 py-5 bg-[var(--panel)] border-y border-[var(--border)]">
        <span className="font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">{t("landing.strip.label")}</span>
        {stripItems.map((label, i) => (
          <span key={label} className="flex items-center gap-2 text-[var(--ink-muted)] font-semibold text-sm">
            <span className="text-[var(--accent)]">{stripIcons[i]}</span>
            {label}
          </span>
        ))}
      </div>

      {/* Hoe het werkt */}
      <section id="hoe-het-werkt" className="px-[22px] sm:px-11 py-12 sm:py-[74px] max-w-[1200px] mx-auto scroll-mt-20">
        <SectionHead eyebrow={t("landing.how.eyebrow")} title={t("landing.how.title")} sub={t("landing.how.sub")} />
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          {steps.map((step, i) => (
            <div key={step.title} className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg)] p-6 sm:p-7">
              <span className="font-mono-badge font-semibold text-[13px] text-[var(--accent)] bg-[var(--accent-soft)] w-[34px] h-[34px] rounded-full grid place-items-center mb-4">{i + 1}</span>
              <h3 className="text-xl font-display font-semibold tracking-tight">{step.title}</h3>
              <p className="text-[var(--ink-muted)] text-[14.5px] mt-2 leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Functies */}
      <section id="functies" className="bg-[var(--panel)] scroll-mt-20">
        <div className="px-[22px] sm:px-11 py-12 sm:py-[74px] max-w-[1200px] mx-auto">
          <SectionHead eyebrow={t("landing.featureCards.eyebrow")} title={t("landing.featureCards.title")} />
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-5">
            {features.map((f, i) => (
              <article key={f.title} className="flex gap-4 sm:gap-[18px] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg)] p-6 sm:p-[26px] shadow-[var(--shadow)]">
                <span
                  className={`w-[46px] h-[46px] flex-none rounded-[13px] grid place-items-center ${f.warm ? "bg-[var(--warm-soft)] text-[var(--warm)]" : "bg-[var(--accent-soft)] text-[var(--accent)]"}`}
                >
                  {featureIcons[i]}
                </span>
                <div>
                  <h3 className="text-lg font-display font-semibold tracking-tight">{f.title}</h3>
                  <p className="text-[var(--ink-muted)] text-[14.5px] mt-2 leading-relaxed">{f.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="px-[22px] sm:px-11 py-12 sm:py-[74px] max-w-[1200px] mx-auto">
        <figure className="max-w-[54ch] mx-auto text-center m-0">
          <blockquote className="font-serif-italic text-[20px] sm:text-[27px] leading-snug text-[var(--ink)]">
            “{t("landing.proof.quote")}”
          </blockquote>
          <figcaption className="mt-5 flex items-center justify-center gap-3 text-[var(--ink-muted)] text-sm">
            <span className="w-[38px] h-[38px] rounded-full bg-[var(--accent)] text-white grid place-items-center font-mono-badge font-semibold">{t("landing.proof.avatar")}</span>
            <span>{t("landing.proof.cite")}</span>
          </figcaption>
        </figure>
      </section>

      {/* CTA band */}
      <section className="px-[22px] sm:px-11 pb-12 sm:pb-[74px]">
        <div
          className="relative overflow-hidden rounded-[24px] px-7 py-9 sm:px-14 sm:py-14 text-center text-white shadow-[var(--shadow-blue)] max-w-[1200px] mx-auto"
          style={{ background: "linear-gradient(135deg, var(--accent), var(--accent-hover))" }}
        >
          <span
            aria-hidden="true"
            className="absolute -right-12 -top-12 sm:-right-20 sm:-top-20 w-40 h-40 sm:w-72 sm:h-72 rounded-full"
            style={{ background: "var(--warm-soft)" }}
          />
          <div className="relative">
            <h2 className="text-[27px] sm:text-[40px] font-display font-semibold tracking-tight text-white">{t("landing.cta.title")}</h2>
            <p className="text-white/85 text-[15px] sm:text-[18px] mt-3.5 mb-7 max-w-[48ch] mx-auto">{t("landing.cta.sub")}</p>
            {/* One-off white-on-accent button (no new Button variant per brand §5). */}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-[var(--accent)] font-semibold rounded-[var(--radius)] h-12 px-6 text-base transition-[transform,opacity] duration-150 ease-out hover:-translate-y-px active:translate-y-px hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
            >
              {t("landing.cta.button")}
              <ArrowRight />
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
